import React, { useRef, useState } from "react";
import { Formik } from "formik";
import CMSFormTextArea from "../../formFields/CMSFormTextArea";
import { useToast } from "../../contexts/ToastContext";
import ContainerLoading from "../../common/loading/ContainerLoading";
import { productReviewSchema } from "../../utils/validations";

const ReviewForm = ({ order, onSubmit }) => {
  const deliveredProducts = order?.products || [];
  const { addToast } = useToast();

  // Status per product
  const [status, setStatus] = useState(deliveredProducts.map(() => "idle"));

  // Create refs array once for all products
  const formRefs = useRef(
    deliveredProducts.map(() => ({
      rating: React.createRef(),
      feedback: React.createRef(),
    }))
  );

  const scrollToFirstError = (refs, errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    setTimeout(() => {
      el.focus?.({ preventScroll: true });
    }, 300);
  };

  return (
    <div className="mt-6 flex flex-col gap-6 bg-[#DEE2DD]/40 rounded-[15px] p-6 lg:px-10 w-full ">
        <h3 className="text-24pt">Leave a Review</h3>

      <div className="grid grid-cols-1 gap-6 mb-4">
        {deliveredProducts.map((product, idx) => {
          const refs = formRefs.current[idx];

          return (
            <Formik
              key={idx}
              initialValues={{
                rating: 0,
                feedback: "",
              }}
              validationSchema={productReviewSchema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                const newStatus = [...status];
                newStatus[idx] = "loading";
                setStatus(newStatus);

                try {
                  await new Promise((resolve, reject) => {
                    setTimeout(() => {
                      Math.random() < 0.2
                        ? reject(new Error("Network error"))
                        : resolve();
                    }, 1000);
                  });

                  newStatus[idx] = "success";
                  setStatus([...newStatus]);
                  addToast(`Submitted Review for ${product.name}`, "success");
                  if (onSubmit) onSubmit(product, values);

                  setTimeout(() => {
                    resetForm();
                    newStatus[idx] = "idle";
                    setStatus([...newStatus]);
                  }, 2000);
                } catch (err) {
                  newStatus[idx] = "idle";
                  setStatus([...newStatus]);
                  addToast(
                    `Review submission failed for ${product.name}. Please try again.`,
                    "error"
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {(form) => (
                <form
                  className="flex flex-col gap-4 bg-white p-4 rounded-md shadow-sm relative"
                  onSubmit={(e) => {
                    e.preventDefault();
                    form.handleSubmit();
                    setTimeout(
                      () => scrollToFirstError(refs, form.errors),
                      100
                    );
                  }}
                >
                  {status[idx] === "loading" && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
                      <ContainerLoading />
                    </div>
                  )}

                  <div className="flex items-center gap-6 mb-2">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-14 h-14 rounded object-cover"
                    />
                    <p className="font-medium">{product.name}</p>
                  </div>

                  {/* Rating */}
                  <div className="flex gap-1 mb-2 relative" ref={refs.rating}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => form.setFieldValue("rating", star)}
                        className={`text-3xl ${
                          form.values.rating >= star
                            ? "text-orange"
                            : "text-gray"
                        }`}
                      >
                        ★
                      </button>
                    ))}
                    {form.errors.rating && form.touched.rating && (
                      <p className="errorMessage">{form.errors.rating}</p>
                    )}
                  </div>

                  {/* Feedback */}
                  <div ref={refs.feedback}>
                    <CMSFormTextArea
                      form={form}
                      name="feedback"
                      maxLength={300}
                      label="Write your feedback"
                      required={false}
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={
                        form.isSubmitting ||
                        status[idx] === "loading" ||
                        status[idx] === "success"
                      }
                      className={`generalButton flex items-center gap-1 px-2  rounded-md font-mono transition whitespace-nowrap text-20pt px-4 text-white ${
                        status[idx] === "loading" || form.isSubmitting
                          ? "bg-gray cursor-not-allowed"
                          : status[idx] === "success"
                          ? "bg-green cursor-not-allowed"
                          : "bg-green hover:bg-green-700"
                      }`}
                    >
                      {status[idx] === "loading" || form.isSubmitting
                        ? "Submitting..."
                        : status[idx] === "success"
                        ? "Submitted!"
                        : "Submit"}
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          );
        })}
      </div>
    </div>
  );
};

export default ReviewForm;
